import React from 'react';
import _ from 'underscore';
import classNames from 'classnames';
import { CellMeasurerCache, CellMeasurer } from 'react-virtualized';
import { VirtualizedTreeViewProps } from './shapes';
import { getFlattenedTree, getExtensions, getRowIndexFromId, setNodeExpandState } from './nodes';
import { ListViewWithInfiniteLoader, ListView } from './list-component';

/*
* MIT License
* Copyright (c) 2018 Diogo Cunha
* Permission is hereby granted, free of charge, to any person obtaining a copy
* of this software and associated documentation files (the "Software"), to deal
* in the Software without restriction, including without limitation the rights
* to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
* copies of the Software, and to permit persons to whom the Software is
* furnished to do so, subject to the following conditions:
* The above copyright notice and this permission notice shall be included in all
* copies or substantial portions of the Software.
* THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
* IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
* FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
* AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
* LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
* OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
* SOFTWARE.

* The MIT License (MIT)
* Copyright (c) 2015 Brian Vaughn
* Permission is hereby granted, free of charge, to any person obtaining a copy
* of this software and associated documentation files (the "Software"), to deal
* in the Software without restriction, including without limitation the rights
* to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
* copies of the Software, and to permit persons to whom the Software is
* furnished to do so, subject to the following conditions:
* The above copyright notice and this permission notice shall be included in all
* copies or substantial portions of the Software.
* THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
* IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
* FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
* AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
* LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
* OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
* SOFTWARE.
*/

export class VirtualizedTree extends React.PureComponent {
  cellMeasurerCache = new CellMeasurerCache({
    fixedWidth: true,
    minHeight: this.props.cellHeight,
  });

  rowRenderer = ({
    node, key, measure, style, NodeRenderer,
  }) => {
    const updatedStyle = _.extend(
      {},
      style,
      { paddingLeft: node.deepness * this.props.nodePaddingLeft }
    );

    return (
      <div
        key={key}
        className={classNames('tree-node', this.props.getClassNames(node))}
        style={updatedStyle}
        role="treeitem"
      >
        <NodeRenderer node={node} onChange={this.handleChange} measure={measure} />
      </div>
    );
  };

  handleChange = ({
    node, type,
  }) => {
    const updatedNodes = getExtensions(this.props.extensions)
      .updateTypeHandlers[type](this.props.nodes, node);

    this.props.onChange(updatedNodes);
  };

  nodesRowRenderer = nodes => ({
    key, index, style, parent,
  }) => {
    const NodeRenderer = this.props.children;
    const node = nodes[index];

    return (
      <CellMeasurer
        cache={this.cellMeasurerCache}
        columnIndex={0}
        key={key}
        rowIndex={index}
        parent={parent}
      >
        {m => this.rowRenderer(_.extend(m, {
          node, key, style, NodeRenderer,
        }))}
      </CellMeasurer>
    );
  };

  filternodeDeepFunction = () => {
    let filteredNodesWithParents = [];
    const {
      nodes, filterNode, isSearch,
    } = this.props;
    if (filterNode) {
      filteredNodesWithParents = setNodeExpandState(nodes, filterNode, isSearch);
    }
    let flattenedTree = getFlattenedTree(nodes);
    if (filteredNodesWithParents.length > 0) {
      flattenedTree = _.filter(flattenedTree, node => filteredNodesWithParents.includes(node.id));
    }
    return flattenedTree;
  };

  render = () => {
    let flattenedTree = this.props.getFlattenedTree ?
      this.props.getFlattenedTree(this.props.nodes) :
      getFlattenedTree(this.props.nodes);
    if (this.props.filterNodeDeep) {
      flattenedTree = _.isFunction(this.props.filterNodeDeep)
        ? this.props.filterNodeDeep(this.props.nodes, this.props.filterNode, this.props.isSearch)
        : this.filternodeDeepFunction();
    } else if (this.props.filterNode) {
      flattenedTree = _.filter(flattenedTree, node => this.props.filterNode(node));
    }

    const rowCount = this.props.rowCount ? this.props.rowCount : flattenedTree.length;
    const scrollToIndex = getRowIndexFromId(flattenedTree, this.props.scrollToId);
    const ChildTreeView = this.props.enableOnScrollLoad ?
      ListViewWithInfiniteLoader :
      ListView;
    let heightStyle = {};

    if (this.props.autoHeight) {
      let height = flattenedTree.length * this.props.cellHeight;

      if (this.props.maxHeight) {
        height = Math.min(height, this.props.maxHeight);
      }

      heightStyle = { height };
    }

    if (this.props.shouldClearCellMeasurerCache) {
      // keep the cached width and height of the nodes before the scrollToIndex
      const keepCache = {};
      _.each(_.range(scrollToIndex + 1), (rowIdx) => {
        if (this.cellMeasurerCache.has(rowIdx)) {
          keepCache[rowIdx] = {
            height: this.cellMeasurerCache.getHeight(rowIdx),
            width: this.cellMeasurerCache.getWidth(rowIdx),
          };
        }
      });
      this.cellMeasurerCache.clearAll();
      _.each(keepCache, (value, rowIdx) => {
        this.cellMeasurerCache.set(rowIdx, 0, value.width, value.height);
      });
    }

    return (
      <div className={this.props.treeViewClassNames} style={heightStyle} role="tree">
        <ChildTreeView
          ariaLabel={this.props.ariaLabel}
          deferredMeasurementCache={this.cellMeasurerCache}
          flattenedTree={flattenedTree}
          rowRenderer={this.nodesRowRenderer}
          rowCount={rowCount}
          rowHeight={this.cellMeasurerCache.rowHeight}
          scrollToIndex={scrollToIndex}
          onScrollOptions={this.props.onScrollOptions}
          isRowLoaded={({ index }) =>
            this.props.isRowLoaded({
              index,
              node: flattenedTree[index],
            })
          }
          loadMoreRows={({ startIndex, stopIndex }) =>
            this.props.loadMoreRows({
              startIndex,
              stopIndex,
              nodes: flattenedTree,
            })
          }
        />
        {_.isEmpty(flattenedTree) && this.props.placeholder && <div className="tree-view-placeholder">{this.props.placeholder}</div>}
      </div>
    );
  };
}

VirtualizedTree.propTypes = VirtualizedTreeViewProps;
